// Dropping a `head.dna` on the content browser: the non-DOM half.
//
// The UI (wizards/OrlDnaDirectUpload.jsx) is a progress bar around this
// function. Everything that can be wrong — the extension, the upload name, the
// stage a failure happened in — is decided here, where a node:test can drive it
// without a browser, a wasm build or a 39 MB DNA on disk.
//
// The upload name is NOT a literal: `orlAssetName(ORL_PACK_FILE)` is the one
// statement of what the loader looks for (bundleFiles.js), so a rename there
// moves the uploader with it instead of silently producing a pack nothing reads.
//
// No step swallows anything. Each rejection is re-thrown with the stage it died
// in attached (`err.stage`), because "conversion failed" and "upload failed"
// need different fixes and a merged message tells the user neither.

import { ORL_PACK_FILE, orlAssetName } from "./bundleFiles.js";

/** The extension the content browser dispatches on. */
export const ORL_DNA_EXTENSION = "dna";

/** The /ogs asset name a converted pack is uploaded under. */
export const orlPackUploadName = () => orlAssetName(ORL_PACK_FILE);

/** Ordered stages the UI renders. `done` is terminal. */
export const ORL_DNA_STAGES = ["reading", "converting", "uploading", "done"];

/** True for a MetaHuman DNA filename. Extension only — the byte signature is
 *  checked by dnaToPack, which is the layer that can actually read it. */
export function isOrlDnaFile(name) {
  return /\.dna$/i.test(String(name ?? ""));
}

/**
 * Wrap the packed bytes as an upload-ready File under the bundle asset name.
 * `FileCtor` is injectable so a node test can assert the name/size without
 * depending on the runtime's File implementation.
 */
export function packAsUploadFile(packBytes, { FileCtor = globalThis.File } = {}) {
  if (!(packBytes instanceof Uint8Array)) {
    throw new Error("[orl] packAsUploadFile: expected the pack as a Uint8Array");
  }
  if (typeof FileCtor !== "function") {
    throw new Error("[orl] packAsUploadFile: no File constructor in this environment");
  }
  return new FileCtor([packBytes], orlPackUploadName(), { type: "application/octet-stream" });
}

function tag(err, stage) {
  const e = err instanceof Error ? err : new Error(String(err));
  e.stage = stage;
  return e;
}

/**
 * Read a dropped `.dna`, convert it to the ORL pack, upload the pack.
 *
 * Every collaborator is injected — there is no default network or worker here,
 * so this module can never reach a server from a test.
 *
 * @param {{name: string, size?: number}} file          the dropped file
 * @param {object}   deps
 * @param {(file) => Promise<ArrayBuffer|Uint8Array>} deps.readBytes
 * @param {(bytes: Uint8Array, o: {onLog}) => Promise<Uint8Array>} deps.convert
 * @param {(packFile, o: {onProgress}) => Promise<any>} deps.upload
 * @param {(stage: string, detail?: object) => void} [deps.onStage]
 * @param {Function} [deps.FileCtor]
 * @returns {Promise<{row: any, packBytes: Uint8Array, name: string}>}
 */
export async function convertAndUploadDna(file, { readBytes, convert, upload, onStage = () => {}, FileCtor } = {}) {
  if (!file || !file.name) throw new Error("[orl] convertAndUploadDna: no file");
  if (!isOrlDnaFile(file.name)) {
    throw new Error(`[orl] convertAndUploadDna: "${file.name}" is not a .dna`);
  }
  for (const [k, fn] of [["readBytes", readBytes], ["convert", convert], ["upload", upload]]) {
    if (typeof fn !== "function") throw new Error(`[orl] convertAndUploadDna: ${k} is required`);
  }

  onStage("reading", { name: file.name, size: file.size ?? 0 });
  let dnaBytes;
  try {
    const raw = await readBytes(file);
    dnaBytes = raw instanceof Uint8Array ? raw : new Uint8Array(raw);
  } catch (err) {
    throw tag(err, "reading");
  }
  if (!dnaBytes.length) throw tag(new Error(`[orl] "${file.name}" is empty`), "reading");

  onStage("converting", { bytes: dnaBytes.length });
  let packBytes;
  try {
    packBytes = await convert(dnaBytes, { onLog: (message) => onStage("converting", { message }) });
  } catch (err) {
    throw tag(err, "converting");
  }
  if (!(packBytes instanceof Uint8Array) || !packBytes.length) {
    throw tag(new Error("[orl] the converter returned no pack bytes"), "converting");
  }

  const name = orlPackUploadName();
  onStage("uploading", { name, bytes: packBytes.length });
  let row;
  try {
    const packFile = packAsUploadFile(packBytes, FileCtor ? { FileCtor } : undefined);
    row = await upload(packFile, { onProgress: (p) => onStage("uploading", { name, progress: p }) });
  } catch (err) {
    throw tag(err, "uploading");
  }

  onStage("done", { name, bytes: packBytes.length, row });
  return { row, packBytes, name };
}

export default convertAndUploadDna;
